import { Building2, Search, CheckCircle } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { useNavigate } from 'react-router-dom';
import { getCompanies } from '@/lib/dataService';

const CompanySelectorSidebar = ({ selectedCompanyId, onSelectCompany }) => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    getCompanies()
      .then(data => setCompanies(data))
      .catch(err => console.error('Failed to load companies:', err));
  }, []);

  const filteredCompanies = companies.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.registrationNumber.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isOverdue = (date) => {
    if (!date) return false;
    return new Date(date) < new Date();
  };

  return (
    <div className="w-80 border-r border-border bg-card flex flex-col h-screen" data-testid="company-selector-sidebar">
      <div className="p-4 border-b border-border">
        <h3 className="text-sm font-semibold mb-3">SELECT COMPANY</h3>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search companies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {filteredCompanies.length === 0 ? (
          <div className="text-center py-12 px-4">
            <Building2 className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
            <p className="text-sm text-muted-foreground mb-3">No companies found</p>
            <button
              onClick={() => navigate('/companies/add')}
              className="text-sm text-primary hover:underline"
            >
              Add your first company
            </button>
          </div>
        ) : (
          <div className="space-y-1">
            {filteredCompanies.map((company) => (
              <button
                key={company.id}
                onClick={() => onSelectCompany && onSelectCompany(company)}
                className={`w-full text-left p-3 rounded-lg transition-colors ${
                  selectedCompanyId === company.id
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-accent'
                }`}
                data-testid="company-option"
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    selectedCompanyId === company.id ? 'bg-primary-foreground/20' : 'bg-muted'
                  }`}>
                    <Building2 className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-sm truncate">{company.name}</p>
                      {selectedCompanyId === company.id && (
                        <CheckCircle className="w-4 h-4 flex-shrink-0" />
                      )}
                    </div>
                    <p className={`text-xs font-mono ${
                      selectedCompanyId === company.id ? 'opacity-80' : 'text-muted-foreground'
                    }`}>
                      {company.registrationNumber}
                    </p>
                    {company.accountsDue && (
                      <p className={`text-xs mt-1 ${
                        isOverdue(company.accountsDue)
                          ? 'text-red-600 font-medium'
                          : selectedCompanyId === company.id
                          ? 'opacity-80'
                          : 'text-muted-foreground'
                      }`}>
                        Due: {company.accountsDue}
                      </p>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanySelectorSidebar;